<script>
// SidebarLink: Handles navigation links in the sidebar, highlights active route
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { collapsed } from '../utils/state';

export default {
    props: {
        to: { type: String, required: true },     // Target route or URL
        icon: { type: String, required: false },  // Icon class or SVG path
        iconComponent: { type: [Object, Function], required: false }, // Custom icon component
        external: { type: Boolean, default: false }
    },
    setup(props) {
        const route = useRoute();
        // Only check active for internal links
        const isActive = computed(() => !props.external && route.path === props.to);

        // Check if icon is an SVG path (ends with .svg or starts with / or ./)
        const isSvgIcon = computed(() =>
            props.icon &&
            (props.icon.endsWith('.svg') || props.icon.startsWith('/') || props.icon.startsWith('./'))
        );

        // Hide text for 0.2s when collapsed changes
        const showText = ref(!collapsed.value);
        watch(collapsed, (val) => {
            showText.value = false;
            setTimeout(() => {
                showText.value = !val;
            }, 200);
        });

        return { isActive, collapsed, isSvgIcon, showText };
    }
}
</script>

<template>
    <a
        v-if="external"
        :href="to"
        class="link"
        :class="{ collapsed: collapsed }"
        rel="noopener"
    >
        <span v-if="iconComponent">
            <component :is="iconComponent" class="icon" />
        </span>
        <span
            v-else-if="isSvgIcon"
            class="icon svg-icon"
            :style="{ backgroundImage: `url('${icon}')` }"
        />
        <i v-else class="icon" :class="icon" />
        <transition name="fade">
            <span v-if="showText && !collapsed" style="padding-right: 100%;">
                <slot />
            </span>
        </transition>
    </a>
    <router-link
        v-else
        :to="to"
        class="link"
        :class="{ active: isActive, collapsed: collapsed }"
    >
        <span v-if="iconComponent">
            <component :is="iconComponent" class="icon" />
        </span>
        <span
            v-else-if="isSvgIcon"
            class="icon svg-icon"
            :style="{ backgroundImage: `url('${icon}')` }"
        />
        <i v-else class="icon" :class="icon" />
        <transition name="fade">
            <span v-if="showText && !collapsed" style="padding-right: ;">
                <slot />
            </span>
        </transition>
    </router-link>
</template>

<style scoped>
.fade-enter-active {
    transition: opacity 0.2s;
}
.fade-leave-active {
    transition: none;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}

.link {
    display: flex;
    align-items: center;

    cursor: pointer;
    position: relative;
    user-select: none;
    margin: .5em .5em .5em 1em;
    padding: 0.5em;
    border-radius: 50%;

    color: #94918B;
    text-decoration: none;
    transition: background 0.2s, color 0.2s, padding 0.2s;
    
    width: 100%; /* Make link fill sidebar width */
    box-sizing: border-box; /* Ensure padding doesn't overflow */
    font-size: 1em;
}

/* Collapsed state: reduce left/right padding */
.link.collapsed {
    padding-left: 0em;
    padding-right: 0.5em;
}

.link.collapsed .icon {
    margin-left: 0.25em;
}

.link:hover .icon {
    color: var(--primary-color);
}
.link:hover {
    color: var(--primary-color);
}

.link.active .icon {
    color: #087DB5;
}
.link.active {
    color: #087DB5;
}

.link .icon {
    flex-shrink: 0;
    width: 35px;
    font-size: 1.5em;
    transition: color 0.2s;
    margin-right: 0.5em;
    transition: 0.2s;
}

/* SVG icon style */
.link .svg-icon {
    display: inline-block;
    width: 2em;
    height: 2em;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 0.5em;
    vertical-align: middle;
}

/* Add pseudo-element for hover background */
.link::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: 50px;
    background: #fff; /* Subtle blue, adjust as needed */
    z-index: 0;
    opacity: 0;
    transition: 
        opacity 0.2s,
        width 0.2s;
    pointer-events: none;
}

/* Add pseudo-element for active background */
.link::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: 50px;
    background: #fff; /* Subtle blue, adjust as needed */
    z-index: 0;
    opacity: 0;
    transition: 
        opacity 0.2s,
        width 0.2s;
    pointer-events: none;
}

/* When active, expand background past right edge */
.link.active::after {
    opacity: 1;
    width: 120%; /* Extend 20% past right edge */
    left: 0;
}

/* Ensure content is above the background */
.link > * {
    position: relative;
    z-index: 1;
}
</style>